import {OrderChangeRequestStatus} from '@hconnect/tuc-processes/orderManagement'
import {useQuery} from '@tanstack/react-query'
import {useCallback} from 'react'

import {queries} from '../../../../common/react-query/queries'
import {TransactionResultType} from '../types/orderChangeResult.types'

import {useOrderRequestResultPolling} from './useOrderRequestResultPolling'

/** Interval between polling requests in milliseconds */
const CHANGE_ORDER_POLL_INTERVAL_MS = 3000

/** Maximum number of polling attempts before giving up */
const CHANGE_ORDER_POLL_MAX_RETRIES = 10

/**
 * Internal hook that fetches the current change request status from the API v2.
 *
 * @param orderId - The ID of the order being changed
 * @param requestId - The ID of the change request returned from mutation
 * @returns React Query result with change request data
 * @internal
 */
const useChangeRequestById = (orderId: string, requestId?: string) => {
  return useQuery<OrderChangeRequestStatus, Error>({
    queryKey: ['changeOrder', orderId, 'request', requestId],
    queryFn: () => queries.orderChangeRequestById(orderId, requestId!),
    enabled: requestId !== undefined
  })
}

/**
 * Hook that polls the change request status until completion.
 *
 * After submitting a change request, this hook continuously checks
 * the status every 3 seconds (up to 10 times) until the request is either
 * successful, failed, or times out.
 *
 * @param orderId - The ID of the order being changed
 * @param requestId - The ID of the change request returned from mutation
 * @returns Object containing:
 *   - `triggerPolling`: Function to start the polling process
 *   - `isPolling`: Boolean indicating if polling is currently active
 *   - `result`: The final result status (success/failure/pending) or undefined if still polling
 *
 * @example
 * ```tsx
 * const { triggerPolling, isPolling, result } = useChangeOrderPolling(order.orderId, requestId);
 *
 * // Start polling after API call succeeds
 * const response = await changeOrderMutation.mutateAsync(params);
 * triggerPolling();
 *
 * // Check result
 * useEffect(() => {
 *   if (result) {
 *     navigation.navigate('Result', { type: result });
 *   }
 * }, [result]);
 * ```
 */
export const useChangeOrderPolling = (
  orderId: string,
  requestId?: string
): {
  triggerPolling: () => void
  isPolling: boolean
  result: TransactionResultType | undefined
} => {
  const changeRequestQuery = useChangeRequestById(orderId, requestId)

  const getUpdatedStatus = useCallback(async (): Promise<TransactionResultType | undefined> => {
    const queryInfo = await changeRequestQuery.refetch()
    if (queryInfo.data) {
      const {status, requestData} = queryInfo.data

      if (status === 'completed') {
        return requestData?.requiresManualApproval ? 'pendingDispatch' : 'success'
      }

      if (status === 'failed') {
        return 'failure'
      }
    }
    return undefined
  }, [changeRequestQuery])

  const {triggerPolling, isPolling, result} = useOrderRequestResultPolling(
    CHANGE_ORDER_POLL_INTERVAL_MS,
    CHANGE_ORDER_POLL_MAX_RETRIES,
    getUpdatedStatus
  )

  return {
    triggerPolling,
    isPolling,
    result
  }
}
